type SortableValue = string | boolean | number | Date | null | undefined;

// Firebase Timestamp interface (adjust based on your actual Timestamp type)
interface Timestamp {
  toDate(): Date;
  seconds: number;
  nanoseconds: number;
}

// Allow any object type to be passed to sorting functions
type Sortable = SortableValue | Timestamp | Record<string, unknown>;

/**
 * Transforms the given value into a sortable format (string or number) for comparison.
 * @param {Sortable} value - The value to be transformed; it can be a string, number, boolean, Date, or a Timestamp object.
 * @returns {string | number} The transformed value suitable for sorting, where strings are normalized to lowercase and other types are converted for numeric or lexicographical comparison.
 */
export function getSortableValue(value: Sortable): string | number {
  // Handle null/undefined - sort to end
  if (value === null || value === undefined) {
    return "zzz_null"; // String that sorts last
  }

  // Handle Timestamp - convert to milliseconds for numeric comparison
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().getTime();
  }

  // Handle Date - convert to milliseconds
  if (value instanceof Date) {
    return value.getTime();
  }

  // Handle boolean - convert to number for consistent sorting
  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }

  // Handle number
  if (typeof value === "number") {
    return value;
  }

  // Handle string - convert to lowercase for case-insensitive sorting
  if (typeof value === "string") {
    return value.toLowerCase();
  }

  // Fallback for any other type
  return String(value).toLowerCase();
}

/**
 * Compares two values, which can either be sortable values or timestamps, and returns a numerical result indicating their sort order.
 * @param {Sortable} a - The first value to compare.
 * @param {Sortable} b - The second value to compare.
 * @param {boolean} [desc] - Optional boolean indicating whether to sort in descending order. Defaults to `false`.
 * @returns {number} A numeric value indicating the relative order of the two values:
 *                  A negative number if `a` should come before `b`,
 *                  zero if they are considered equal,
 *                  or a positive number if `a` should come after `b`.
 */
export function compareValues(
  a: Sortable,
  b: Sortable,
  desc: boolean = false
): number {
  const aVal = getSortableValue(a);
  const bVal = getSortableValue(b);

  // Both are numbers (including dates converted to timestamps)
  if (typeof aVal === "number" && typeof bVal === "number") {
    const result = aVal - bVal;
    return desc ? -result : result;
  }

  // Both are strings (including booleans and other values converted to strings)
  if (typeof aVal === "string" && typeof bVal === "string") {
    const result = aVal.localeCompare(bVal);
    return desc ? -result : result;
  }

  // Mixed types - numbers come before strings
  if (typeof aVal === "number" && typeof bVal === "string") {
    return desc ? 1 : -1;
  }

  if (typeof aVal === "string" && typeof bVal === "number") {
    return desc ? -1 : 1;
  }

  // Fallback
  return 0;
}
